/**
 * Formats a `YYYY-MM-DD` digest date as a long weekday label, e.g.
 * `Fri Jul 10, 2026`. The date parts are read directly (not through
 * `new Date(string)`) so a UTC-parsed midnight can't shift the day in the
 * viewer's local timezone.
 */
export function formatLongDate(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(date);
  if (!match) return date;
  const [, year, month, day] = match;
  return formatLongDateLocal(new Date(Number(year), Number(month) - 1, Number(day)));
}

/**
 * Formats a `Date` as a long weekday label in the viewer's local timezone,
 * e.g. `Fri Jul 10, 2026`.
 */
export function formatLongDateLocal(date: Date): string {
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Format an instant's calendar date in the viewer's timezone, preserving its UTC offset. */
export function formatTimestampDate(iso: string): string {
  // Older PostgreSQL OF projections emit +00 rather than the ISO-required +00:00.
  const normalized = iso.replace(/(T\d{2}:\d{2}:\d{2}(?:\.\d+)?[+-]\d{2})$/u, '$1:00');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return iso;
  return formatLongDateLocal(date);
}

/**
 * Parses a `YYYY-MM-DD` string into a local `Date` at midnight, reading the
 * parts directly so a UTC-parsed midnight can't shift the day in the viewer's
 * timezone. Returns `null` for malformed input.
 */
export function parseLocalDate(date: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(date);
  if (!match) return null;
  const [, year, month, day] = match;
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  const local = new Date(y, m - 1, d);
  // Reject out-of-range values (e.g. `2026-99-99`) that JS would silently roll
  // over into a valid Date. Verify the parts round-trip unchanged.
  if (local.getFullYear() !== y || local.getMonth() !== m - 1 || local.getDate() !== d) {
    return null;
  }
  return local;
}

/** English ordinal suffix for a day-of-month, e.g. 1 → "st", 22 → "nd". */
function ordinalSuffix(day: number): string {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/**
 * Formats a `YYYY-MM-DD` date as a long weekday + ordinal day, e.g.
 * `Friday, July 10th`. Parsed via {@link parseLocalDate} to avoid UTC day-shift.
 */
export function formatOrdinalDate(date: string): string {
  const local = parseLocalDate(date);
  if (!local) return date;
  const weekday = local.toLocaleDateString(undefined, { weekday: 'long' });
  const month = local.toLocaleDateString(undefined, { month: 'long' });
  const day = local.getDate();
  return `${weekday}, ${month} ${day}${ordinalSuffix(day)}`;
}

/** Formats a `YYYY-MM-DD` date as a short weekday, e.g. `Fri`. */
export function formatWeekdayShort(date: string): string {
  const local = parseLocalDate(date);
  if (!local) return date;
  return local.toLocaleDateString(undefined, { weekday: 'short' });
}

/** Formats a `YYYY-MM-DD` date as a short month + day, e.g. `Jun 25`. */
export function formatMonthDay(date: string): string {
  const local = parseLocalDate(date);
  if (!local) return date;
  return local.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Renders a `Date` as a local `YYYY-MM-DD` string (no UTC conversion). */
export function toLocalYmd(local: Date): string {
  const year = local.getFullYear();
  const month = String(local.getMonth() + 1).padStart(2, '0');
  const day = String(local.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Formats an ISO timestamp as a short local time, e.g. `5:04p`. */
export function formatShortTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const meridiem = hours < 12 ? 'a' : 'p';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${minutes}${meridiem}`;
}
